import {
  TEntityResponse,
  TEntityListResponse,
  TCreatePlanReqBody,
  TPlanResBody,
  TPlanSummaryResBody,
  TUpdatePlanReqBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TPlansClient = {
  create(body: TCreatePlanReqBody): Promise<TEntityResponse<'plan', TPlanResBody> | undefined>;
  update(id: string, body: TUpdatePlanReqBody): Promise<TEntityResponse<'plan', TPlanResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'plan', TPlanResBody> | undefined>;
  list(
    limit: number,
    page: number,
    type: 'all' | 'main' | 'extra'
  ): Promise<TEntityListResponse<'plan', TPlanSummaryResBody> | undefined>;
  list_filtered(
    limit: number,
    page: number,
    type: 'all' | 'main' | 'extra',
    filter: 'area' | 'company',
    id: string
  ): Promise<TEntityListResponse<'plan', TPlanSummaryResBody> | undefined>;
};

function PlansClient(apiClient: TAPIClient): TPlansClient {
  return {
    async create(body: TCreatePlanReqBody): Promise<TEntityResponse<'plan', TPlanResBody> | undefined> {
      const data = await apiClient.post<TCreatePlanReqBody, TEntityResponse<'plan', TPlanResBody>>('/plans', body);

      if (data && data.type !== 'plan') {
        throw new Error('Invalid entity type received, expected: plan, received: ' + data.type);
      }

      return data;
    },

    async update(id: string, body: TUpdatePlanReqBody): Promise<TEntityResponse<'plan', TPlanResBody> | undefined> {
      const data = await apiClient.put<TUpdatePlanReqBody, TEntityResponse<'plan', TPlanResBody>>(`/plans/${id}`, body);

      if (data && data.type !== 'plan') {
        throw new Error('Invalid entity type received, expected: plan, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/plans/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'plan', TPlanResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'plan', TPlanResBody>>(`/plans/${id}`);

      if (data && data.type !== 'plan') {
        throw new Error('Invalid entity type received, expected: plan, received: ' + data.type);
      }

      return data;
    },

    async list_filtered(
      limit: number,
      page: number,
      type: 'all' | 'main' | 'extra',
      filter: 'area' | 'company',
      id: string
    ): Promise<TEntityListResponse<'plan', TPlanSummaryResBody> | undefined> {
      const selectedFilter = filter === 'area' ? 'area_id' : 'company_id';

      let data;
      if (type === 'main') {
        data = await apiClient.get<TEntityListResponse<'plan', TPlanSummaryResBody>>(
          `/plans?filter[]=main&filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
        );
      } else if (type === 'extra') {
        data = await apiClient.get<TEntityListResponse<'plan', TPlanSummaryResBody>>(
          `/plans?filter[]=extra&filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'plan', TPlanSummaryResBody>>(
          `/plans?filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
        );
      }

      if (data && data.type !== 'plan') {
        throw new Error('Invalid entity type received, expected: plan, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number,
      type: 'all' | 'main' | 'extra'
    ): Promise<TEntityListResponse<'plan', TPlanSummaryResBody> | undefined> {
      let data;
      if (type === 'main') {
        data = await apiClient.get<TEntityListResponse<'plan', TPlanSummaryResBody>>(
          `/plans?filter[]=main&limit=${limit}&page=${page}`
        );
      } else if (type === 'extra') {
        data = await apiClient.get<TEntityListResponse<'plan', TPlanSummaryResBody>>(
          `/plans?filter[]=extra&limit=${limit}&page=${page}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'plan', TPlanSummaryResBody>>(
          `/plans?limit=${limit}&page=${page}`
        );
      }

      if (data && data.type !== 'plan') {
        throw new Error('Invalid entity type received, expected: plan, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TPlansClient, PlansClient };
